import React, { useState, useEffect, useRef } from "react";

import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { StripeService } from "../services/stripeService";
import { useAuth } from "../contexts/AuthContext";

// ─── Animated Peak Score Radial ───────────────────────────────────────────────
const getPeakScoreColor = (score: number): string => {
  if (score >= 100) return "#DAA520";
  if (score >= 80) return "#0096c7";
  if (score >= 60) return "#22c55e";
  if (score >= 30) return "#f97316";
  return "#ef4444";
};

const AnimatedPeakScoreMini = () => {
  const [animatedScore, setAnimatedScore] = useState(60);
  const componentRef = useRef<HTMLDivElement>(null);
  const startScore = 60;
  const targetScore = 100;
  const baseScore = 110;

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    let pauseTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const runAnimation = () => {
      const duration = 2000;
      const steps = 60;
      const scoreRange = targetScore - startScore;
      const increment = scoreRange / steps;
      let currentStep = 0;

      setAnimatedScore(startScore);

      timer = setInterval(() => {
        if (cancelled) {
          clearInterval(timer);
          return;
        }
        currentStep++;
        if (currentStep <= steps) {
          setAnimatedScore(
            Math.min(startScore + currentStep * increment, targetScore),
          );
        } else {
          clearInterval(timer);
          // Pause at the top, then restart
          pauseTimer = setTimeout(() => {
            if (!cancelled) runAnimation();
          }, 2000);
        }
      }, duration / steps);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnimation();
          } else {
            cancelled = true;
            clearInterval(timer);
            clearTimeout(pauseTimer);
            cancelled = false;
          }
        });
      },
      { threshold: 0.3 },
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      cancelled = true;
      clearInterval(timer);
      clearTimeout(pauseTimer);
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, []);

  const peakPercentage = Math.min((animatedScore / baseScore) * 100, 100);
  const peakColor = getPeakScoreColor(animatedScore);
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(peakPercentage / 100) * circumference} ${circumference}`;

  return (
    <div
      ref={componentRef}
      className="relative inline-flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full"
      style={{ width: "8rem", height: "8rem" }}
    >
      <svg
        className="transform -rotate-90"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth="6"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={peakColor}
          strokeWidth="6"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          className="transition-all duration-300 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-bold text-5xl text-white font-heading"
          aria-label={`Peak score: ${Math.round(animatedScore)}`}
        >
          {Math.round(animatedScore)}
        </span>
      </div>
    </div>
  );
};

// ─── Feature lists matching SubscriptionSettings ────────────────────────────
const AI_FEATURES = new Set([
  "Personalised AI Insights & Analysis",
  "The Wizard AI",
  "Custom Insights & Wizard Tone",
]);

const INCLUDED_FEATURES: Record<string, string[]> = {
  plus: [
    "Peak Score & Peak Readiness",
    "2 Wearable Devices",
    "Load Balance",
    "Heart Lung Recovery Index (HLR)",
    "Activity & Exercise Analytics",
    "Saved Session Templates",
    "Export Data to CSV",
    "Performance Goals",
  ],
  premium: [
    "Everything in Plus",
    "3 Wearable Devices",
    "Weekly, Monthly, 12 Week & Custom Date Range Analytics",
    "Personalised AI Insights & Analysis",
    "The Wizard AI",
    "Custom Insights & Wizard Tone",
  ],
};

const CARD_STYLE: Record<string, { gradient: string; title: string }> = {
  plus: {
    gradient: "bg-base-100",
    title: "Plus",
  },
  premium: {
    gradient: "bg-base-100",
    title: "Premium",
  },
};

interface UpgradePricingCardProps {
  userCountry?: string;
  className?: string;
  showBillingToggle?: boolean;
  defaultBillingPeriod?: "month" | "year";
}

const UpgradePricingCard: React.FC<UpgradePricingCardProps> = ({
  className = "",
  showBillingToggle = true,
  defaultBillingPeriod = "year",
}) => {
  const { currentUser, userData } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<"month" | "year">(
    defaultBillingPeriod,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  // Helper function to format price with currency symbol
  const formatPrice = (price: number, currency: string) => {
    const currencySymbols: Record<string, string> = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      AUD: "$",
      CAD: "$",
      INR: "₹",
      ZAR: "R",
    };
    const symbol = currencySymbols[currency] || currency + " ";

    // Format large amounts without decimals (INR, ZAR)
    if ((currency === "INR" || currency === "ZAR") && price >= 1000) {
      return `${symbol}${price.toLocaleString("en-IN")}`;
    }
    return `${symbol}${price.toFixed(2)}`;
  };

  // Helper function to format duration labels for display
  const formatDuration = (duration: string) => {
    const normalized = duration?.toLowerCase();
    switch (normalized) {
      case "quarter":
        return "3 months";
      case "year":
        return "year";
      case "month":
        return "month";
      default:
        return normalized;
    }
  };

  const handleUpgrade = async (productType: "plus" | "premium") => {
    try {
      if (!currentUser) throw new Error("User not authenticated.");
      setUpgrading(true);

      const priceId = StripeService.getPeakPriceId(productType, billingPeriod);
      const trialDays = userData?.hasUsedTrial ? 0 : 14;
      const successUrl = `${window.location.origin}/settings/subscription?upgrade_success=true`;
      const cancelUrl = `${window.location.origin}/settings/subscription?upgrade_canceled=true`;

      const sessionUrl = await StripeService.createCheckoutSession(
        priceId,
        "",
        successUrl,
        cancelUrl,
        currentUser.uid,
        productType === "plus" ? "Plus" : "Premium",
        trialDays,
      );

      if (sessionUrl) window.location.href = sessionUrl;
    } catch (error) {
      alert(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <>
      <div className={className}>
        <div className="bg-gradient-to-br from-primary to-blue-800 md:rounded-xl rounded-none shadow-sm border border-base-200">
          {/* Header */}
          <div className="flex items-center p-8 pb-0 mb-6">
            <div className="text-left text-base-content flex-1">
              <h4 className="font-geist font-light text-white text-sm mb-2">
                Your training, elevated.
              </h4>
              <h3 className="text-4xl font-bold font-grotesk text-white mb-1">
                Train Smarter. <br></br>
                Perform Better.
              </h3>
              <p className="text-md text-white">
                Stop guessing. Know your readiness, plan your sessions, and
                train with clarity every day.
              </p>
            </div>
            <div className="hidden md:block flex-shrink-0 ml-6">
              <AnimatedPeakScoreMini />
              <p className="text-white font-bold text-md text-center">
                Peak Score
              </p>
            </div>
          </div>
          <div className="overflow-hidden relative space-y-2">
            {/* First row - scrolling left */}
            <div className="flex gap-3 animate-marquee">
              {[...Array(2)].map((_, setIndex) => (
                <React.Fragment key={setIndex}>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Peak Readiness Score
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Schedule Sessions
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Default Library of 500+ Exercises
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Connect Up to 3 Wearables
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Individualised AI Insights
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Advanced Analytics
                  </span>
                </React.Fragment>
              ))}
            </div>
            {/* Second row - scrolling right */}
            <div className="flex gap-3 animate-marquee-reverse">
              {[...Array(2)].map((_, setIndex) => (
                <React.Fragment key={setIndex}>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Export Data to CSV
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Priority Support
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Training Planner
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Monthly Analytics
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Custom Date Ranges
                  </span>
                  <span className="bg-blue-900/30 text-sm text-white py-1 px-3 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4" />
                    Elite Features Access
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="flex justify-start pt-8 pb-8 pr-8 pl-8">
            <button
              onClick={() => setIsDialogOpen(true)}
              className="bg-lime text-black px-6 py-3 rounded-full font-grotesk font-bold  transition-colors w-full"
            >
              Start 14 Day Free Trial
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Dialog */}
      {isDialogOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md flex items-end sm:items-center justify-center z-[60] sm:p-8"
          onClick={() => setIsDialogOpen(false)}
        >
          <div
            className="bg-gradient-to-br p-4 from-primary to-blue-800 w-full h-full sm:h-auto sm:max-w-3xl sm:rounded-xl shadow-xl sm:max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dialog Header */}
            <div className="flex items-center justify-between p-6 pb-0">
              <h2 className="text-2xl font-bold font-grotesk text-white">
                Elevate your Training
              </h2>
              <button
                onClick={() => setIsDialogOpen(false)}
                className="p-2 text-base-content/50 hover:text-base-content rounded-full hover:bg-base-300 transition-colors"
                aria-label="Close dialog"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Dialog Content */}
            <div className="p-4 sm:p-6 pt-4">
              {/* Billing Period Toggle — pill-shaped */}
              {showBillingToggle && (
                <div className="flex justify-center mb-6">
                  <fieldset
                    aria-label="Payment frequency"
                    className="w-full max-w-sm"
                  >
                    <div className="flex bg-base-content rounded-full p-1 shadow-inner">
                      {(
                        [
                          { value: "month", label: "Monthly" },
                          { value: "year", label: "Annually" },
                        ] as const
                      ).map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex-1 cursor-pointer rounded-full px-3 py-2 text-center text-sm font-semibold transition-all duration-200 relative ${
                            billingPeriod === opt.value
                              ? "text-black shadow-md bg-white"
                              : "text-base-100"
                          }`}
                        >
                          <input
                            type="radio"
                            name="billing-frequency-dialog"
                            value={opt.value}
                            checked={billingPeriod === opt.value}
                            onChange={() => setBillingPeriod(opt.value)}
                            className="sr-only"
                          />
                          <span>{opt.label}</span>
                          {opt.value === "year" && (
                            <span className="absolute -top-3 -right-2 bg-lime text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full sm:inline-flex">
                              -17%
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              )}

              {/* Plus & Premium Cards — flat USD pricing (new PEAK account) */}
              <div className="flex flex-col md:flex-row gap-4">
                {(["plus", "premium"] as const).map((productType) => {
                    const style = CARD_STYLE[productType];
                    const currency = "USD";
                    const price = StripeService.getPeakDisplayPrice(
                      productType,
                      billingPeriod,
                    );
                    const pricePerMonth =
                      billingPeriod === "month" ? price : price / 12;
                    const pricePerWeek =
                      billingPeriod === "month" ? price / 4.33 : price / 52;

                    return (
                      <div
                        key={productType}
                        className={`${style.gradient} rounded-xl p-6 flex-1 flex flex-col relative`}
                      >
                        {/* {productType === "premium" && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-purple-500 text-white-content shadow-md">
                          INTELLIGENCE LAYER
                        </span>
                      )} */}
                        <h2 className=" text-2xl font-bold font-geist text-base-content mb-1">
                          {style.title}
                        </h2>
                        <p className="text-sm text-base-content/70 font-geist mb-3">
                          {productType === "plus"
                            ? "Train with clarity."
                            : "Intelligent training tools."}
                        </p>

                        <div className="mb-1">
                          <span className="text-4xl font-bold text-base-content font-grotesk">
                            {formatPrice(price, currency)}
                          </span>
                          <span className="text-base-content/80 ml-2 text-sm">
                            / {formatDuration(billingPeriod)}
                          </span>
                        </div>

                        <div className="flex gap-2 mb-4 text-xs mt-2">
                          <div className="border border-base-content/20 px-4 py-2 flex items-center font-geist rounded-md bg-base-content/5 text-base-content/60">
                            <strong>
                              {formatPrice(pricePerMonth, currency)}
                            </strong>
                            /month
                          </div>
                          <div className="border border-base-content/20 px-4 py-2 flex items-center font-geist rounded-md bg-base-content/5 text-base-content/60">
                            <strong>
                              {formatPrice(pricePerWeek, currency)}
                            </strong>
                            /week
                          </div>
                        </div>

                        {/* Included features */}
                        <div className="mb-5 flex-1">
                          <h3 className="text-base-content/60 text-xs font-medium uppercase tracking-wide mb-2">
                            Includes
                          </h3>
                          <ul className="space-y-1.5">
                            {INCLUDED_FEATURES[productType]?.map(
                              (feature, idx) => {
                                const isAI = AI_FEATURES.has(feature);
                                return (
                                  <li
                                    key={idx}
                                    className={`flex items-start gap-2 text-sm text-base-content/90 ${isAI ? " rounded-md px-2 -mx-2" : ""}`}
                                  >
                                    <span className="mt-0.5 flex-shrink-0 text-base-content">
                                      {isAI ? "✨" : "✓"}
                                    </span>
                                    <span>{feature}</span>
                                  </li>
                                );
                              },
                            )}
                          </ul>
                        </div>

                        <button
                          onClick={() => handleUpgrade(productType)}
                          disabled={upgrading}
                          className={`w-full py-3 px-4 rounded-full font-bold font-grotesk transition-colors duration-200 flex items-center justify-center gap-2 ${
                            upgrading
                              ? "bg-white/20 text-base-content/60 cursor-default"
                              : "bg-lime text-black hover:bg-lime/80"
                          }`}
                        >
                          {upgrading ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : userData?.hasUsedTrial ? (
                            `Upgrade to ${style.title}`
                          ) : (
                            "Start. 14 Day Free Trial"
                          )}
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpgradePricingCard;
